/**
 * V5 报告页展示词表 —— 把后端 code（ability / trait / layer / L 级 / tier）映射到中文显示标签和前端图标。
 *
 * ⚠️ 这不是 schema 真源。Schema 真源是后端：
 *    - backend/apps/reports/schemas/v5_report_schema.py
 *    - backend/apps/dimensions/defaults.py (8 能力 canonical)
 *    - backend/apps/dimensions/trait_models.py (3 素质 canonical)
 *
 * 本文件只用于把后端 code 渲染成 UI 上的中文名/图标，避免在 React 组件里散落硬编码。
 *
 * 长期方案见 docs/archive/v5/AI-HANDOFF.md：
 *  - ability/trait 中文名最好由后端在 report payload 里带出来（或单独 labels API），
 *    避免后台改名要发版（V5-升级规划 §7.5）。在那之前这里保持与 defaults.py 对齐。
 *  - L 级中文名 / trait tier label 见下方注释，各自来源不同。
 */

// ---------------------------------------------------------------------------
// 8 能力（与 backend/apps/dimensions/defaults.py CANONICAL_DIMENSIONS 对齐）
// 顺序 = canonical sort_order（雷达图 / 进度条按此顺序绘制）
// ---------------------------------------------------------------------------
export const ABILITY_LABELS = {
  cognitive_understanding:   { name: '认知理解力',   layer: 'cognitive_prep', layer_sort: 1, sort: 1 },
  learning_adaptation:       { name: '学习适应力',   layer: 'cognitive_prep', layer_sort: 2, sort: 2 },
  scenario_judgment:         { name: '场景判断力',   layer: 'application',    layer_sort: 1, sort: 3 },
  instruction_interaction:   { name: '指令交互力',   layer: 'application',    layer_sort: 2, sort: 4 },
  task_orchestration:        { name: '任务编排力',   layer: 'application',    layer_sort: 3, sort: 5 },
  result_evaluation:         { name: '结果评估力',   layer: 'value_output',   layer_sort: 1, sort: 6 },
  safety_responsibility:     { name: '安全责任力',   layer: 'value_output',   layer_sort: 2, sort: 7 },
  innovation_transformation: { name: '创新转化力',   layer: 'value_output',   layer_sort: 3, sort: 8 },
};

export const ABILITY_ORDER = Object.keys(ABILITY_LABELS).sort(
  (a, b) => ABILITY_LABELS[a].sort - ABILITY_LABELS[b].sort,
);

// ---------------------------------------------------------------------------
// 3 层（与 defaults.py layer 字段对齐；中文名来自 V5-升级规划 §1.1）
// ---------------------------------------------------------------------------
export const LAYER_LABELS = {
  cognitive_prep: { name: '认知准备层', sort: 1 },
  application:    { name: '应用执行层', sort: 2 },
  value_output:   { name: '价值产出层', sort: 3 },
};

export const LAYER_ORDER = ['cognitive_prep', 'application', 'value_output'];

// ---------------------------------------------------------------------------
// 3 素质（与 trait_models.CANONICAL_TRAITS 对齐 + 前端图标 hint）
// ---------------------------------------------------------------------------
export const TRAIT_LABELS = {
  ai_willingness:       { name: 'AI 意愿',  icon: 'heart',          sort: 1 },
  independent_thinking: { name: '独立思考', icon: 'brain',          sort: 2 },
  proactive_learning:   { name: '主动学习', icon: 'graduation-cap', sort: 3 },
};

export const TRAIT_ORDER = ['ai_willingness', 'independent_thinking', 'proactive_learning'];

// ---------------------------------------------------------------------------
// L 级中文名（来自 V5-升级规划 §1.4，已冻结）
// ---------------------------------------------------------------------------
export const L_GRADE_LABELS = {
  L1: '知晓尝试级',
  L2: '基础应用级',
  L3: '协同优化级',
  L4: '创新引领级',
};

// ---------------------------------------------------------------------------
// Assessment depth → 报告版本中文名
// (V5-升级规划 §7.3.1：基础版 / 标准版 / 专业版；设计稿"快速版"已弃)
// ---------------------------------------------------------------------------
export const DEPTH_VERSION_LABELS = {
  quick:    '基础版',
  standard: '标准版',
  deep:     '专业版',
};

// ---------------------------------------------------------------------------
// 素质 tier —— 后端 scoring.py map_to_trait_tier() 直接返回中文标签（"高水平"/...
// /"基础水平"），不是 code。所以这里**只用于** UI 上 chip 的颜色/强度分档。
// ---------------------------------------------------------------------------
export const TIER_INTENSITY = {
  '高水平':   { rank: 4, tone: 'strong' },
  '较高水平': { rank: 3, tone: 'high' },
  '中等水平': { rank: 2, tone: 'mid' },
  '基础水平': { rank: 1, tone: 'low' },
};

// 兼容素质 narrative 里看到"中等偏高"等 V4 残留写法（M5 prompt 还未稳定时的容错）
export const TIER_ALIASES = {
  '中等偏高': '较高水平',
  '中等偏低': '中等水平',
};
